import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {PublishingScreencloudPanelProps} from './publishing-screencloud-panel.types';
import {Text, TextSize} from '@Components/text';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {buildIframeEmbedSnippet, useCopyButton} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.hooks';
import {useProductUsed} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {DigitalSignageOption} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useAppDispatch} from '@/hooks';
import {usePosterPageHashedId} from '@/components/panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';

export function PublishingScreencloudPanel({...props}: PublishingScreencloudPanelProps): ReactElement<any> {
  const [generatedIframe, setGeneratedIframe] = useState('');
  const [clicked, setClicked] = useState(false);
  const productUsed = useProductUsed();
  const dispatch = useAppDispatch();
  const pageHashedId = usePosterPageHashedId();
  const pageOrderId = productUsed?.existingPageOrders?.[pageHashedId] ?? productUsed?.orderId;

  useEffect(() => {
    setTimeout(() => {
      setClicked(false);
    }, 3000);
  }, [clicked]);

  useEffect(() => {
    try {
      if (pageOrderId) {
        void window.PMW.readLocal('v/generateDisplayCode', {
          orderId: pageOrderId,
        })
          .then((response: string) => {
            const generatedIframe = buildIframeEmbedSnippet(response, productUsed ?? ({} as PosterProductDataForProcessDesign));
            setGeneratedIframe(generatedIframe);
          })
          .catch(() => {
            dispatch(setErrorOnPublishPanel(true));
            dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
          });
      }
    } catch (e) {
      console.error(e);

      dispatch(setErrorOnPublishPanel(true));
      dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
    }
  }, [pageOrderId]);

  const listItemElement: ReactElement<any> = (
    <div className="content-sub-text">
      <Text val={window.i18next.t('pmwjs_screencloud_follow_steps')} size={TextSize.XSMALL} />
      <div className="flexbox flex-v-row">
        <Text dangerouslySetInnerHTML className="spacing-p-b-2" val={window.i18next.t('pmwjs_screencloud_step1')} size={TextSize.XSMALL} />
        <Text className="spacing-p-b-2" val={window.i18next.t('pmwjs_screencloud_step2')} size={TextSize.XSMALL} />
        <Text val={window.i18next.t('pmwjs_screencloud_step3')} size={TextSize.XSMALL} />
      </div>
    </div>
  );

  const onCopyLink = (): void => {
    void window.PMW.writeLocal('cart/addOrderUsedByDigitalSignage', {
      orderId: pageOrderId,
      type: DigitalSignageOption.SCREENCLOUD,
    });
  };

  return (
    <DigitalSignageSubPanel
      panelHeading={window.i18next.t('pmwjs_screencloud')}
      heading={window.i18next.t('pmwjs_screencloud_digital_signage')}
      showButton
      showInputBar
      inputBarIcon="icon-code"
      onClose={props.onClose}
      listItem={listItemElement}
      webpageLink={generatedIframe}
      onClick={() => {
        void window.PMW.copyToClipboard(generatedIframe);
        onCopyLink();
      }}
      buttonComponent={useCopyButton(clicked, generatedIframe, setClicked, onCopyLink)}
      imgSrc="publishing-screencloud.png"
    />
  );
}
